import { useState, useRef } from "react";
import api, { photoUrl } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Camera, Save, UserCircle } from "lucide-react";
import { toast } from "sonner";

const ROLE_LABEL = { coordenador: "Coordenador", lider: "Líder de PGD", membro: "Membro", visitante: "Visitante" };

function userPhotoUrl(path) {
  return path ? photoUrl(path).replace("/group-photo/", "/user-photo/") : null;
}

export default function Profile() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || "", phone: user?.phone || "",
    address: user?.address || "", birth_date: user?.birth_date || "",
  });
  const [saving, setSaving] = useState(false);
  const [avatar, setAvatar] = useState(user?.avatar_path || null);
  const fileRef = useRef();
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/auth/me", form);
      setUser(data);
      toast.success("Perfil atualizado!");
    } catch {
      toast.error("Erro ao salvar");
    }
    setSaving(false);
  };

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/auth/me/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAvatar(data.avatar_path);
      const me = await api.get("/auth/me");
      setUser(me.data);
      toast.success("Foto atualizada!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao enviar foto");
    }
    e.target.value = "";
  };

  return (
    <div className="space-y-8 animate-fade-up max-w-2xl" data-testid="profile-page">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Meu Perfil</h1>
        <p className="text-muted-foreground mt-1">Atualize seus dados de contato e sua foto.</p>
      </div>

      <Card className="p-6">
        <div className="flex items-center gap-5">
          <div className="relative">
            {avatar ? (
              <img src={userPhotoUrl(avatar)} alt="avatar" className="h-24 w-24 rounded-full object-cover border-2 border-border" />
            ) : (
              <div className="h-24 w-24 rounded-full bg-gradient-to-br from-accent to-primary flex items-center justify-center text-3xl font-bold text-white">
                {user?.name?.charAt(0)?.toUpperCase()}
              </div>
            )}
            <button
              onClick={() => fileRef.current?.click()}
              className="absolute bottom-0 right-0 h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center border-2 border-background"
              data-testid="profile-photo-button"
            >
              <Camera className="h-4 w-4" />
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadAvatar} />
          </div>
          <div>
            <div className="font-display text-xl font-semibold">{user?.name}</div>
            <div className="text-sm text-muted-foreground">{user?.email}</div>
            <Badge className="mt-2 bg-primary/15 text-primary border-none">{ROLE_LABEL[user?.role] || user?.role}</Badge>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <form onSubmit={save} className="space-y-4">
          <div>
            <Label>Nome completo</Label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5" data-testid="profile-name-input" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Telefone</Label>
              <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1.5" data-testid="profile-phone-input" />
            </div>
            <div>
              <Label>Data de nascimento</Label>
              <Input type="date" value={form.birth_date} onChange={(e) => set("birth_date", e.target.value)} className="mt-1.5" data-testid="profile-birthdate-input" />
            </div>
          </div>
          <div>
            <Label>Endereço</Label>
            <Input value={form.address} onChange={(e) => set("address", e.target.value)} className="mt-1.5" data-testid="profile-address-input" />
          </div>
          <Button type="submit" disabled={saving} className="gap-2" data-testid="profile-save-button">
            <Save className="h-4 w-4" /> {saving ? "Salvando…" : "Salvar alterações"}
          </Button>
        </form>
      </Card>
    </div>
  );
}
